import React, { useEffect, useState } from 'react';
import {
  collection, query, where, getDocs, addDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { toDateStr, formatTimeRange } from './scheduleUtils';
import { useBodyScrollLock } from '../../utilities/useBodyScrollLock';

const CLAIM_COLLECTION = 'shiftSwapRequests';

// "9a", "9:30a", "2p", "2:30p"
const compactTime = (ts) => {
  if (!ts?.toDate) return '';
  const d = ts.toDate();
  const h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? 'p' : 'a';
  const h12 = h % 12 || 12;
  return m === 0 ? `${h12}${ampm}` : `${h12}:${String(m).padStart(2, '0')}${ampm}`;
};

// "9a–5p"
const compactRange = (start, end) => `${compactTime(start)}–${compactTime(end)}`;

/**
 * EmployeeScheduleView — read-only schedule for standard employees.
 * Interactive monthly calendar: tap any shift day to see details + swap/drop.
 */
const EmployeeScheduleView = ({ user, restaurantId }) => {
  const [shifts, setShifts]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [employees, setEmployees]   = useState([]);
  const [openShifts, setOpenShifts] = useState([]);
  const [claimedIds, setClaimedIds] = useState(new Set());
  const [selectedDay, setSelectedDay] = useState(null); // "YYYY-MM-DD" | null
  const [calMonth, setCalMonth]     = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });

  const employeeId = user.employeeId;

  useEffect(() => {
    if (!employeeId) { setLoading(false); return; }
    loadShifts();
    loadEmployees();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  useEffect(() => {
    if (!loading && user.position) loadOpenShifts(shifts);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shifts, loading]);

  const loadShifts = async () => {
    setLoading(true);
    try {
      const lookBack = new Date();
      lookBack.setHours(0, 0, 0, 0);
      lookBack.setDate(lookBack.getDate() - 7);

      const schedulesSnap = await getDocs(
        collection(db, 'restaurants_data', restaurantId, 'schedules')
      );
      const allShifts = [];
      await Promise.all(
        schedulesSnap.docs
          .filter(d => d.data().status === 'published')
          .map(async schedDoc => {
            const snap = await getDocs(
              query(
                collection(db, 'restaurants_data', restaurantId, 'schedules', schedDoc.id, 'shifts'),
                where('employeeId', '==', employeeId)
              )
            );
            snap.docs.forEach(sd => {
              const data = sd.data();
              if (data.date >= toDateStr(lookBack)) {
                allShifts.push({ id: sd.id, scheduleId: schedDoc.id, ...data });
              }
            });
          })
      );
      allShifts.sort((a, b) => a.date.localeCompare(b.date));
      setShifts(allShifts);
    } catch (err) {
      console.error('Failed to load shifts:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const snap = await getDocs(collection(db, 'restaurants_data', restaurantId, 'employees'));
      setEmployees(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Failed to load employees:', err);
    }
  };

  const loadOpenShifts = async (myShifts) => {
    if (!user.position) return;
    try {
      const todayStr = toDateStr(new Date());
      const schedulesSnap = await getDocs(
        collection(db, 'restaurants_data', restaurantId, 'schedules')
      );
      const myShiftDates = new Set((myShifts || []).map(s => s.date));
      const found = [];
      await Promise.all(
        schedulesSnap.docs
          .filter(d => d.data().status === 'published')
          .map(async schedDoc => {
            const snap = await getDocs(
              query(
                collection(db, 'restaurants_data', restaurantId, 'schedules', schedDoc.id, 'shifts'),
                where('isOpenShift', '==', true)
              )
            );
            snap.docs.forEach(sd => {
              const data = sd.data();
              if (data.role === user.position && data.date >= todayStr && !myShiftDates.has(data.date)) {
                found.push({ id: sd.id, scheduleId: schedDoc.id, ...data });
              }
            });
          })
      );
      found.sort((a, b) => a.date.localeCompare(b.date));
      setOpenShifts(found);
    } catch (err) {
      console.error('Failed to load open shifts:', err);
    }
  };

  const handleClaim = async (shift) => {
    try {
      await addDoc(
        collection(db, 'restaurants_data', restaurantId, CLAIM_COLLECTION),
        {
          type: 'claim',
          shiftId: shift.id,
          scheduleId: shift.scheduleId,
          shiftRole: shift.role,
          shiftDate: shift.date,
          shiftStart: shift.startTime,
          shiftEnd: shift.endTime,
          requesterId: user.employeeId,
          requesterName: `${user.firstName} ${user.lastName}`,
          targetEmployeeId: null,
          targetEmployeeName: null,
          status: 'pending',
          requesterNote: null,
          reviewedBy: null,
          reviewedByName: null,
          reviewedAt: null,
          createdAt: serverTimestamp(),
        }
      );
      setClaimedIds(prev => new Set([...prev, shift.id]));
    } catch (err) {
      console.error('Failed to submit claim:', err);
    }
  };

  // ── Calendar helpers ─────────────────────────────────────────────────────────

  // Map dateStr → sorted shifts for that day
  const shiftsByDate = shifts.reduce((m, s) => {
    if (!m[s.date]) m[s.date] = [];
    m[s.date].push(s);
    return m;
  }, {});

  const today    = new Date();
  const todayStr = toDateStr(today);

  const prevMonth = () => setCalMonth(({ year, month }) =>
    month === 0 ? { year: year - 1, month: 11 } : { year, month: month - 1 }
  );
  const nextMonth = () => setCalMonth(({ year, month }) =>
    month === 11 ? { year: year + 1, month: 0 } : { year, month: month + 1 }
  );

  const monthStart    = new Date(calMonth.year, calMonth.month, 1);
  const daysInMonth   = new Date(calMonth.year, calMonth.month + 1, 0).getDate();
  const firstDayOfWeek = (monthStart.getDay() + 6) % 7; // Mon = 0

  const calDays = [
    ...Array(firstDayOfWeek).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => {
      const d = new Date(calMonth.year, calMonth.month, i + 1);
      return { date: d, str: toDateStr(d) };
    }),
  ];

  const selectedShifts = selectedDay ? (shiftsByDate[selectedDay] || []) : [];

  if (!employeeId) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-neutral-400">Your employee profile hasn't been linked yet.</p>
        <p className="text-sm text-neutral-300 mt-1">Contact your manager to update your account.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* ── Calendar ── */}
      <div className="bg-white border border-neutral-200 rounded-xl p-4">

        {/* Month nav */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={prevMonth}
            className="w-9 h-9 flex items-center justify-center rounded-lg bg-neutral-100 hover:bg-neutral-200 text-neutral-700 font-bold text-lg transition-colors"
          >
            ‹
          </button>
          <h3 className="text-sm font-semibold text-neutral-800">
            {monthStart.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </h3>
          <button
            onClick={nextMonth}
            className="w-9 h-9 flex items-center justify-center rounded-lg bg-neutral-100 hover:bg-neutral-200 text-neutral-700 font-bold text-lg transition-colors"
          >
            ›
          </button>
        </div>

        {/* Day-of-week header */}
        <div className="grid grid-cols-7 mb-1">
          {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map((d, i) => (
            <div key={i} className="text-center text-[10px] font-semibold text-neutral-400 uppercase tracking-wide py-1">
              {d}
            </div>
          ))}
        </div>

        {/* Day cells */}
        <div className="grid grid-cols-7 gap-1">
          {calDays.map((day, i) => {
            if (!day) return <div key={i} />;

            const dayShifts  = shiftsByDate[day.str] || [];
            const hasShifts  = dayShifts.length > 0;
            const isToday    = day.str === todayStr;
            const isSelected = day.str === selectedDay;
            const isPast     = day.str < todayStr;

            return (
              <button
                key={i}
                onClick={() => hasShifts && setSelectedDay(isSelected ? null : day.str)}
                disabled={!hasShifts}
                className={`
                  relative flex flex-col items-center rounded-lg py-1 px-0.5 min-h-[44px] transition-colors
                  ${isSelected
                    ? 'bg-blue-600 ring-2 ring-blue-600'
                    : isToday
                      ? 'bg-neutral-900'
                      : hasShifts
                        ? 'bg-blue-50 active:bg-blue-100 cursor-pointer'
                        : 'cursor-default'
                  }
                `}
              >
                {/* Date number */}
                <span className={`text-sm font-semibold leading-tight ${
                  isSelected ? 'text-white'
                  : isToday ? 'text-white'
                  : hasShifts ? 'text-blue-700'
                  : isPast ? 'text-neutral-300'
                  : 'text-neutral-500'
                }`}>
                  {day.date.getDate()}
                </span>

                {/* Shift time chips */}
                {dayShifts.map((s, j) => (
                  <span
                    key={j}
                    className={`text-[12px] leading-tight font-medium mt-0.5 ${
                      isSelected ? 'text-blue-100' : 'text-blue-600'
                    }`}
                  >
                    {compactRange(s.startTime, s.endTime)}
                  </span>
                ))}
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex gap-4 mt-3 text-sm text-neutral-500">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded bg-blue-50 border border-blue-200 inline-block" />
            Shift
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded bg-neutral-900 inline-block" />
            Today
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded bg-blue-600 inline-block" />
            Selected
          </span>
        </div>
      </div>

      {/* ── Day detail modal ── */}
      {selectedDay && selectedShifts.length > 0 && (
        <DaySheetModal
          dateStr={selectedDay}
          shifts={selectedShifts}
          employees={employees.filter(e => e.id !== employeeId)}
          user={user}
          restaurantId={restaurantId}
          todayStr={todayStr}
          onClose={() => setSelectedDay(null)}
        />
      )}

      {/* Loading state */}
      {loading && (
        <p className="text-sm text-neutral-400 text-center py-4">Loading shifts…</p>
      )}

      {/* ── Available to Claim ── */}
      {user.position && openShifts.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-neutral-700 mb-3">Available to Claim</h3>
          <div className="space-y-2">
            {openShifts.map(shift => {
              const claimed = claimedIds.has(shift.id);
              return (
                <div
                  key={shift.id}
                  className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between"
                >
                  <div>
                    <p className="text-sm font-semibold text-neutral-800">
                      {new Date(shift.date + 'T00:00:00').toLocaleDateString('en-US', {
                        weekday: 'short', month: 'short', day: 'numeric',
                      })}
                    </p>
                    <p className="text-sm text-neutral-500 mt-0.5">
                      {formatTimeRange(shift.startTime, shift.endTime)} · {shift.role}
                      {shift.zone ? ` · ${shift.zone}` : ''}
                    </p>
                    {shift.notes && (
                      <p className="text-sm text-neutral-400 mt-0.5 italic">"{shift.notes}"</p>
                    )}
                  </div>
                  <button
                    onClick={() => !claimed && handleClaim(shift)}
                    disabled={claimed}
                    className={`shrink-0 ml-3 text-sm px-3 py-1.5 rounded-lg font-medium transition-colors ${
                      claimed
                        ? 'bg-emerald-100 text-emerald-700 border border-emerald-200 cursor-default'
                        : 'bg-amber-700 text-white hover:bg-amber-800'
                    }`}
                  >
                    {claimed ? 'Request Sent' : 'Claim Shift'}
                  </button>
                </div>
              );
            })}
          </div>
          <p className="text-sm text-neutral-400 mt-2">
            Claim requests are reviewed and approved by your manager.
          </p>
        </div>
      )}
    </div>
  );
};

// ── Day detail modal (bottom sheet on mobile, centered on desktop) ────────────

const DaySheetModal = ({ dateStr, shifts, employees, user, restaurantId, todayStr, onClose }) => {
  useBodyScrollLock();
  const [swapTarget, setSwapTarget] = useState(null);
  const isPast = dateStr < todayStr;

  const dayLabel = new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  });

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black bg-opacity-50 sm:px-4">
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col max-h-[85dvh]" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>

        {/* Header */}
        <div className="px-5 py-4 border-b border-neutral-100 flex items-center justify-between shrink-0">
          <div>
            <p className="text-base font-semibold text-neutral-800">{dayLabel}</p>
            <p className="text-sm text-neutral-400 mt-0.5">
              {shifts.length} shift{shifts.length > 1 ? 's' : ''} scheduled
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-600 text-lg leading-none p-1"
          >
            ✕
          </button>
        </div>

        {/* Shift cards */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-3">
          {shifts.map((shift) => (
            <div
              key={shift.id}
              className="bg-neutral-50 border border-neutral-200 rounded-xl px-4 py-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-neutral-800">{shift.role}</p>
                  <p className="text-sm text-neutral-500 mt-0.5">
                    {formatTimeRange(shift.startTime, shift.endTime)}
                    {shift.zone ? ` · ${shift.zone}` : ''}
                  </p>
                  {shift.notes && (
                    <p className="text-sm text-neutral-400 mt-1 italic">"{shift.notes}"</p>
                  )}
                </div>
                {!isPast && (
                  <button
                    onClick={() => setSwapTarget(shift)}
                    className="shrink-0 text-sm px-3 py-1.5 border border-neutral-300 rounded-lg text-neutral-600 hover:border-amber-400 hover:text-amber-700 transition-colors"
                  >
                    Swap / Drop
                  </button>
                )}
                {isPast && (
                  <span className="shrink-0 text-sm px-2 py-1 bg-neutral-100 text-neutral-400 rounded-full">
                    Past
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

      </div>

      {/* Swap/Drop modal triggered from within */}
      {swapTarget && (
        <SwapDropModal
          shift={swapTarget}
          scheduleId={swapTarget.scheduleId}
          employees={employees}
          user={user}
          restaurantId={restaurantId}
          onClose={() => setSwapTarget(null)}
        />
      )}
    </div>
  );
};

// ── Swap/Drop request modal ───────────────────────────────────────────────────

const SwapDropModal = ({ shift, scheduleId, employees, user, restaurantId, onClose }) => {
  useBodyScrollLock();
  const [type, setType]         = useState('drop');
  const [targetId, setTargetId] = useState('');
  const [note, setNote]         = useState('');
  const [saving, setSaving]     = useState(false);
  const [done, setDone]         = useState(false);

  const targetEmployee = employees.find(e => e.id === targetId);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await addDoc(
        collection(db, 'restaurants_data', restaurantId, 'shiftSwapRequests'),
        {
          shiftId: shift.id,
          scheduleId,
          requesterId: user.employeeId,
          requesterName: `${user.firstName} ${user.lastName}`,
          targetEmployeeId:   type === 'swap' ? (targetId || null) : null,
          targetEmployeeName: type === 'swap' && targetEmployee
            ? `${targetEmployee.firstName} ${targetEmployee.lastName}` : null,
          type,
          status: 'pending',
          requesterNote: note,
          reviewedBy: null,
          reviewedByName: null,
          reviewedAt: null,
          createdAt: serverTimestamp(),
        }
      );
      setDone(true);
    } catch (err) {
      console.error('Failed to submit request:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4 py-6">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden flex flex-col max-h-[90dvh]">
        <div className="px-6 py-4 border-b border-neutral-100 flex items-center justify-between shrink-0">
          <div>
            <h3 className="text-base font-semibold text-neutral-800">Swap or Drop Shift</h3>
            <p className="text-sm text-neutral-400 mt-0.5">
              {shift.role} · {formatTimeRange(shift.startTime, shift.endTime)}
            </p>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-lg">✕</button>
        </div>

        {done ? (
          <div className="px-6 py-8 text-center">
            <p className="text-sm font-medium text-neutral-700 mb-1">Request submitted!</p>
            <p className="text-sm text-neutral-400">Your manager will review and respond.</p>
            <button onClick={onClose} className="mt-4 px-5 py-2 text-sm bg-neutral-900 text-white rounded-lg">
              Close
            </button>
          </div>
        ) : (
          <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1">
            <div className="flex gap-2">
              {['drop', 'swap'].map(t => (
                <button
                  key={t}
                  onClick={() => setType(t)}
                  className={`flex-1 py-2 text-sm font-medium rounded-lg border transition-colors ${
                    type === t
                      ? 'bg-neutral-900 text-white border-neutral-900'
                      : 'border-neutral-300 text-neutral-600'
                  }`}
                >
                  {t === 'drop' ? 'Drop Shift' : 'Request Swap'}
                </button>
              ))}
            </div>

            {type === 'swap' && (
              <div>
                <label className="block text-sm font-medium text-neutral-500 mb-1">
                  Swap with (optional — leave blank to let anyone pick it up)
                </label>
                <select
                  value={targetId}
                  onChange={e => setTargetId(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none"
                >
                  <option value="">Anyone available</option>
                  {employees.map(e => (
                    <option key={e.id} value={e.id}>{e.firstName} {e.lastName}</option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-neutral-500 mb-1">Note (optional)</label>
              <input
                type="text"
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="Reason or context for your manager"
                className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none"
              />
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <button onClick={onClose} className="px-4 py-2 text-sm text-neutral-500 hover:text-neutral-700">
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="px-5 py-2 text-sm font-semibold bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Submitting…' : 'Submit Request'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeScheduleView;
